import React, { useMemo, useRef } from 'react';
import Visualization from './Visualization';
import TimelineVisualization from './TimelineVisualization';
import FlowchartVisualization from './FlowchartVisualization';
import ComparisonVisualization from './ComparisonVisualization';
import HierarchyVisualization from './HierarchyVisualization';
import ProcessVisualization from './ProcessVisualization';

interface VisualizationData {
  subtype: string;
  data: any;
  config: any;
  html?: string;
  tabs?: any[];
}

interface VisualizationRendererProps {
  content: string;
  preview?: boolean;
  isLoading?: boolean;
  collapsed?: boolean;
}

const CHART_SUBTYPES = new Set(['line', 'bar', 'pie', 'donut', 'area', 'scatter', 'radar', 'horizontalBar']);

/**
 * Attempts to parse incomplete JSON by truncating at the last structurally
 * valid position and closing any open brackets/braces. This enables live
 * rendering of chart data as it streams in from the LLM.
 */
function tryParsePartialJSON(content: string): any | null {
  if (!content?.trim()) return null;
  try { return JSON.parse(content); } catch {}

  let str = content.trim();
  let inString = false;
  let escaped = false;
  let lastGoodCut = -1;

  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (escaped) { escaped = false; continue; }
    if (ch === '\\' && inString) { escaped = true; continue; }
    if (ch === '"') {
      if (inString) { inString = false; lastGoodCut = i + 1; }
      else { inString = true; }
      continue;
    }
    if (inString) continue;

    if (ch === '}' || ch === ']') {
      lastGoodCut = i + 1;
    } else if (ch === ',') {
      lastGoodCut = i;
    }
    if (/[\deEtrufalsnul.+-]/.test(ch)) {
      const nextCh = i + 1 < str.length ? str[i + 1] : '';
      if (!nextCh || /[,\]\s}]/.test(nextCh)) {
        lastGoodCut = i + 1;
      }
    }
  }

  if (lastGoodCut <= 0) return null;

  str = str.substring(0, lastGoodCut).replace(/,\s*$/, '');

  inString = false;
  escaped = false;
  const stack: string[] = [];

  for (const ch of str) {
    if (escaped) { escaped = false; continue; }
    if (ch === '\\' && inString) { escaped = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (ch === '{') stack.push('}');
    else if (ch === '[') stack.push(']');
    else if ((ch === '}' || ch === ']') && stack.length > 0) stack.pop();
  }

  while (stack.length > 0) str += stack.pop();

  try { return JSON.parse(str); } catch { return null; }
}

function normalizeVizData(raw: any): VisualizationData | null {
  if (!raw) return null;
  const hasTabs = Array.isArray(raw.tabs) && raw.tabs.length > 0;
  // Tabbed layouts must still pass normalizeVizData: root payload historically used
  // { type: "visualization", tabs } without subtype. Treat non-empty tabs as dashboard.
  if (!raw.subtype && hasTabs) {
    raw = { ...raw, subtype: 'dashboard' };
  }
  if (!raw.subtype) return null;
  if (raw.chartData && !raw.data) raw.data = raw.chartData;
  if (raw.chartConfig && !raw.config) raw.config = raw.chartConfig;
  return {
    subtype: raw.subtype || 'unsupported',
    data: Array.isArray(raw.data) ? raw.data : [],
    config: raw.config || {},
    html: raw.html,
    tabs: raw.tabs,
  };
}

const VisualizationRenderer: React.FC<VisualizationRendererProps> = ({
  content,
  preview = false,
  isLoading = false,
  collapsed = false,
}) => {
  const lastValidRef = useRef<VisualizationData | null>(null);

  const parsedVizData = useMemo((): VisualizationData | null => {
    try {
      return normalizeVizData(JSON.parse(content));
    } catch {
      const partial = tryParsePartialJSON(content);
      return normalizeVizData(partial);
    }
  }, [content]);

  if (parsedVizData) {
    lastValidRef.current = parsedVizData;
  }

  const vizData = parsedVizData ?? lastValidRef.current;

  if (!vizData) {
    if (isLoading) {
      return (
        <div className="viz-loading-placeholder">
          <div className="resource-loading-spinner" />
        </div>
      );
    }
    return <div className="viz-unsupported">Invalid visualization data</div>;
  }

  const renderSingleVisualization = (subtype: string, data: any, config: any, html?: string) => {
    if (CHART_SUBTYPES.has(subtype)) {
      return (
        <Visualization
          subtype={subtype}
          data={data}
          config={config}
          preview={preview}
          isStreaming={isLoading}
          collapsed={collapsed}
        />
      );
    }

    switch (subtype) {
      case 'timeline':
        return <TimelineVisualization data={data} config={config} preview={preview} collapsed={collapsed} />;
      case 'flow':
      case 'flowchart':
        return <FlowchartVisualization data={data} config={config} preview={preview} collapsed={collapsed} />;
      case 'comparison':
        return <ComparisonVisualization data={data} config={config} preview={preview} collapsed={collapsed} />;
      case 'hierarchy':
        return <HierarchyVisualization data={data} config={config} preview={preview} collapsed={collapsed} />;
      case 'process':
        return <ProcessVisualization data={data} config={config} preview={preview} collapsed={collapsed} />;
      default:
        return <div className="viz-unsupported">Unsupported visualization type: {subtype}</div>;
    }
  };

  const renderVisualization = (vizObj: any) => {
    const resolved = vizObj.visualization || vizObj;
    const subtype = resolved.subtype || vizData.subtype;
    const data = resolved.data || vizData.data;
    const config = resolved.config || vizData.config;
    const html = resolved.html || vizObj.html || vizData?.html;
    return renderSingleVisualization(subtype, data, config, html);
  };

  if (vizData.tabs && vizData.tabs.length > 0) {
    return (
      <div className="viz-stacked">
        {vizData.tabs.map((tab: any, index: number) => (
          <div key={index} className="viz-stacked-item">
            {renderVisualization(tab)}
          </div>
        ))}
      </div>
    );
  }

  return renderSingleVisualization(vizData.subtype, vizData.data, vizData.config, vizData.html);
};

export default VisualizationRenderer;
